/**
 * CZNA-507 — the terms of ONE vocabulary type, for offering them as choices.
 *
 * Distinct from `useVocabularyList`, which drives the vocabulary admin PAGE:
 * that hook owns URL query state through `useLocation` and paginates for a
 * table, neither of which a picker inside the form-config editor can use.
 *
 * `isTruncated` is part of the contract, not a detail. The caller renders a
 * restrictive control (a select the admin cannot type into) only when the list
 * on screen is the WHOLE vocabulary — a silently capped list would look
 * identical to a complete one and would quietly make legal values unreachable.
 * The largest vocabulary today is 1 541 terms (VOCABULARY_OCCUPATION, OPEN);
 * the largest CLOSED one is 80 (VOCABULARY_THESAURUS), so the cap is generous
 * for the case that actually restricts input.
 *
 * MEMORY rule: `useOkapiKy()` returns an unstable reference. It is captured in
 * the query-function closure and NEVER listed in a dependency array.
 */
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { fetchVocabularies } from '../api/vocabularyService';
/** How many terms we are willing to pull into a picker in one request. */
export var VOCABULARY_TERM_LIMIT = 500;
export var vocabularyTermsQueryKey = function (type) {
    return ['czna-vocabulary-terms', type !== null && type !== void 0 ? type : null];
};
var useVocabularyTerms = function (type) {
    var _a, _b;
    // Closure-capture only. MEMORY: ky is unstable — never in deps.
    var ky = useOkapiKy();
    var _c = useQuery(vocabularyTermsQueryKey(type), function () { return fetchVocabularies(ky, {
        type: type,
        limit: VOCABULARY_TERM_LIMIT,
        sortBy: 'name',
        sortOrder: 'asc',
    }); }, {
        enabled: Boolean(type),
        // The admin is editing a form, not curating vocabularies; a few minutes
        // of staleness is fine and keeps the editor from refetching per keystroke.
        staleTime: 5 * 60 * 1000,
        retry: false,
    }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError;
    var terms = (_a = data === null || data === void 0 ? void 0 : data.items) !== null && _a !== void 0 ? _a : [];
    var total = (_b = data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _b !== void 0 ? _b : 0;
    return {
        terms: terms,
        total: total,
        isTruncated: total > terms.length,
        // `enabled: false` leaves react-query reporting isLoading — for a caller
        // that passed no type there is nothing loading, so report it honestly.
        isLoading: Boolean(type) && isLoading,
        isError: isError,
    };
};
export default useVocabularyTerms;
